import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import AddIcon from '@mui/icons-material/Add';
import GroupsIcon from '@mui/icons-material/Groups';
import groupService from '../services/GroupService';
import LoadingSpinner from '../components/LoadingSpinner';
import { debugError } from '../utils/debugUtils';

export default function GroupsListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showAll = searchParams.get('view') === 'all';
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);

  const loadGroups = () => {
    setLoading(true);
    setError(null);
    groupService
      .listMine()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        if (!showAll && list.length === 1) {
          navigate(`/groups/${list[0].id}`, { replace: true });
          return;
        }
        setGroups(list);
      })
      .catch((err) => {
        debugError('[GroupsListPage] Error loading groups:', err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) {
      setCreateError(t('groups.nameRequired'));
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const group = await groupService.create(name.trim(), description.trim() || null);
      setCreateOpen(false);
      setName('');
      setDescription('');
      navigate(`/groups/${group.id}`);
    } catch (err) {
      setCreateError(t(`apiErrors.${err.code}`, t('common.error')));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box className="page-content" sx={{ gap: 2, maxWidth: 700, mx: 'auto', width: '100%' }}>
      <Typography
        variant="h5"
        fontWeight={700}
        textAlign="center"
        color="white"
        sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
      >
        {t('groups.title')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          {t('groups.createButton')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<GroupAddIcon />}
          onClick={() => navigate('/groups/join')}
        >
          {t('groups.joinButton')}
        </Button>
      </Box>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <Typography textAlign="center" color="error" py={4}>
          {t('common.error')}
        </Typography>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <GroupsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('groups.empty')}</Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {groups.map((group) => (
            <Card
              key={group.id}
              sx={{ cursor: 'pointer', '&:hover': { boxShadow: 4 } }}
              onClick={() => navigate(`/groups/${group.id}`)}
            >
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {group.name}
                  </Typography>
                  {group.description && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {group.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {t('groups.membersCount', { count: group.memberCount })}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={t(group.myRole === 'ADMIN' ? 'groups.roleAdmin' : 'groups.roleMember')}
                  color={group.myRole === 'ADMIN' ? 'primary' : 'default'}
                />
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          {t('groups.createTitle')}
          <IconButton onClick={() => setCreateOpen(false)} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('groups.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!createError}
            helperText={createError}
            inputProps={{ maxLength: 50 }}
            autoFocus
            fullWidth
          />
          <TextField
            label={t('groups.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: 255 }}
            multiline
            minRows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating}>
            {t('groups.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
